import { Link } from 'react-router-dom';

/**
 * Reusable consent checkbox group used in signup forms (C2C + B2B),
 * candidate add, job publish, and billing flows.
 *
 * Modes:
 *  - signupC2C: ToS+Privacy checkbox + optional marketing checkbox
 *  - signupB2B: Business Terms + DPA + signatory authority (3 required)
 *  - invite: Business Terms + AUP for invited B2B members (2 required)
 *  - addCandidate: lawful basis attestation (1 required)
 *  - publishJob: equal-opportunity attestation (1 required)
 *  - subscribe: subscription terms acceptance (1 required)
 *
 * Pass `values` and `onChange(key, value)` to lift state. Each mode
 * exposes specific keys — see below.
 */
export default function ConsentCheckboxes({ mode, values = {}, onChange = () => {} }) {
  if (mode === 'signupC2C') {
    return (
      <div className="space-y-3 mt-2">
        <CheckboxRow
          checked={Boolean(values.tos_accepted)}
          onChange={(v) => onChange('tos_accepted', v)}
          required
          label={
            <>
              I have read and agree to the{' '}
              <Link to="/terms" target="_blank" className="text-[#F47920] underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" target="_blank" className="text-[#F47920] underline">Privacy Policy</Link>.
              I confirm I am at least 16 years old.
            </>
          }
        />
        <CheckboxRow
          checked={Boolean(values.marketing_consent)}
          onChange={(v) => onChange('marketing_consent', v)}
          label="Send me product updates, interview tips, and career insights by email. (Optional — you can unsubscribe anytime.)"
        />
      </div>
    );
  }

  if (mode === 'signupB2B') {
    return (
      <div className="space-y-3 mt-2">
        <CheckboxRow
          checked={Boolean(values.terms_accepted)}
          onChange={(v) => onChange('terms_accepted', v)}
          required
          label={
            <>
              I agree to the{' '}
              <Link to="/b2b/terms" target="_blank" className="text-[#F47920] underline">Business Terms of Service</Link>.
            </>
          }
        />
        <CheckboxRow
          checked={Boolean(values.dpa_accepted)}
          onChange={(v) => onChange('dpa_accepted', v)}
          required
          label={
            <>
              I agree to the{' '}
              <Link to="/b2b/dpa" target="_blank" className="text-[#F47920] underline">Data Processing Agreement (DPA)</Link>,
              under which Jobskills acts as a data processor for candidate data I upload.
            </>
          }
        />
        <CheckboxRow
          checked={Boolean(values.authorized_signatory)}
          onChange={(v) => onChange('authorized_signatory', v)}
          required
          label="I confirm I am authorized to enter into these agreements on behalf of my organization."
        />
      </div>
    );
  }

  if (mode === 'invite') {
    return (
      <div className="space-y-3 mt-2">
        <CheckboxRow
          checked={Boolean(values.terms_accepted)}
          onChange={(v) => onChange('terms_accepted', v)}
          required
          label={
            <>
              I have read and agree to the{' '}
              <Link to="/b2b/terms" target="_blank" className="text-[#F47920] underline">Business Terms of Service</Link>
              {' '}and the{' '}
              <Link to="/privacy" target="_blank" className="text-[#F47920] underline">Privacy Policy</Link>.
            </>
          }
        />
        <CheckboxRow
          checked={Boolean(values.aup_accepted)}
          onChange={(v) => onChange('aup_accepted', v)}
          required
          label={
            <>
              I agree to abide by the{' '}
              <Link to="/aup" target="_blank" className="text-[#F47920] underline">Acceptable Use Policy</Link>,
              {' '}including non-discrimination and lawful-basis requirements when handling candidate data.
            </>
          }
        />
      </div>
    );
  }

  if (mode === 'addCandidate') {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mt-3">
        <p className="text-sm text-amber-900 mb-3 leading-relaxed">
          You — as the recruiter — are the data controller for this candidate.
          Jobskills acts as your data processor under the{' '}
          <Link to="/b2b/dpa" target="_blank" className="underline">DPA</Link>.
        </p>
        <CheckboxRow
          checked={Boolean(values.lawful_basis_confirmed)}
          onChange={(v) => onChange('lawful_basis_confirmed', v)}
          required
          label={
            <span className="text-[13px] text-gray-800">
              I confirm I have a lawful basis under applicable data-protection laws
              (GDPR/UK GDPR, CCPA, etc.) to process this candidate's personal data
              — for example, the candidate has applied to my organization, given
              consent, or otherwise lawfully shared their data with me. I will
              inform the candidate of this processing where required.
            </span>
          }
        />
      </div>
    );
  }

  if (mode === 'publishJob') {
    return (
      <CheckboxRow
        checked={Boolean(values.compliance_attestation)}
        onChange={(v) => onChange('compliance_attestation', v)}
        required
        label={
          <span className="text-[13px] text-gray-800">
            I confirm this job posting complies with applicable employment and
            equal-opportunity laws (EEOC/Title VII in the US, Equality Act 2010
            in the UK, Directive 2000/78/EC in the EU) and does not contain
            discriminatory criteria.
          </span>
        }
      />
    );
  }

  if (mode === 'subscribe') {
    return (
      <CheckboxRow
        checked={Boolean(values.terms_accepted)}
        onChange={(v) => onChange('terms_accepted', v)}
        required
        label={
          <>
            I agree to the{' '}
            <Link to="/b2b/terms" target="_blank" className="text-[#F47920] underline">Business Terms</Link>,
            {' '}
            <Link to="/b2b/dpa" target="_blank" className="text-[#F47920] underline">DPA</Link>,
            {' '}and{' '}
            <Link to="/refund-policy" target="_blank" className="text-[#F47920] underline">Refund Policy</Link>.
          </>
        }
      />
    );
  }

  return null;
}

function CheckboxRow({ checked, onChange, label, required }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={Boolean(checked)}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 w-4 h-4 accent-[#F47920] flex-shrink-0"
      />
      <span className="text-[13px] text-gray-700 leading-relaxed">
        {label}
        {required ? <span className="text-red-500 ml-1" aria-hidden="true">*</span> : null}
      </span>
    </label>
  );
}
